import {
  createThread,
  fetchMessages,
  fetchThreads,
  cancelRun,
  sendMessage,
  submitActionRequestOutput,
} from "../api";
import {
  assertIsCompletedToolCall,
  type ToolCall,
} from "../component/types/toolCall";
import AnalyticsUtil from "ee/utils/AnalyticsUtil";

/**
 * Service for managing messages and threads
 */
export class MessageService {
  fetchThreads = fetchThreads;
  fetchMessages = fetchMessages;
  createThread = createThread;
  cancelRun = cancelRun;

  /**
   * Send a message to a thread
   *
   * @param params Parameters for sending a message
   * @param signal Optional abort signal for cancellation
   * @returns Promise resolving to the streamed response
   */
  async sendMessage(
    params: {
      threadId: string;
      widgetId: string;
      message: string;
      activeEnv: string;
      queryId: string;
      viewMode: boolean;
      prevResponseId?: string;
    },
    signal?: AbortSignal,
  ): Promise<Response> {
    // Create a modified version of the fetch call that accepts AbortSignal
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Requested-By": "Appsmith",
      Accept: "text/event-stream",
      "X-Appsmith-EnvironmentId": params.activeEnv,
    });

    const anonymousId = AnalyticsUtil.getAnonymousId();

    const requestBody = {
      requestType: "SEND_MESSAGE_STREAM",
      config: {
        threadId: params.threadId,
        resourceId: params.widgetId,
        actionId: params.queryId,
        userName: anonymousId,
        prevResponseId: params.prevResponseId,
      },
      message: params.message,
      viewMode: params.viewMode,
    };

    return sendMessage(requestBody, headers, signal);
  }

  /**
   * Submit action request output back to the server
   *
   * @param params Parameters for submitting action output
   * @param signal Optional abort signal for cancellation
   * @returns Promise resolving to the streamed response
   */
  async submitActionRequestOutput(
    params: {
      widgetId: string;
      queryId: string;
      runId: string;
      threadId: string;
      prevMessageId: string;
      prevResponseId?: string;
      outputs: ToolCall[];
      activeEnv: string;
      viewMode: boolean;
    },
    signal?: AbortSignal,
  ): Promise<Response> {
    // We need to implement our own version with abort support
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Requested-By": "Appsmith",
      Accept: "text/event-stream",
      "X-Appsmith-EnvironmentId": params.activeEnv,
    });

    const anonymousId = AnalyticsUtil.getAnonymousId();

    const toolOutputs = params.outputs.map((toolCall) => {
      assertIsCompletedToolCall(toolCall);

      return {
        toolCallId: toolCall.id,
        name: toolCall.entity.name,
        data:
          toolCall.executionStatus === "success"
            ? JSON.stringify(toolCall.output)
            : JSON.stringify({
                error: toolCall.error,
              }),
      };
    });

    const requestBody = {
      config: {
        threadId: params.threadId,
        resourceId: params.widgetId,
        actionId: params.queryId,
        userName: anonymousId,
        runId: params.runId,
        prevMessageId: params.prevMessageId,
        prevResponseId: params.prevResponseId,
      },
      outputs: toolOutputs,
      viewMode: params.viewMode,
    };

    return submitActionRequestOutput(
      requestBody,
      params.queryId,
      headers,
      signal,
    );
  }
}

// Export a singleton instance
export const messageService = new MessageService();
